import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { InventoryTableFunctionsComponent } from '../inventory-table-functions/inventory-table-functions.component';
import { TranslatePipe } from '@ngx-translate/core';
import { InventoryItemComponent } from '../inventory-item/inventory-item.component';
import { MatDialog } from '@angular/material/dialog';
import { FormsModule } from '@angular/forms';
import { InventoryItemModalComponent } from '../../modal-dialog/inventory-item-modal/inventory-item-modal.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { catchError, EMPTY, finalize, tap } from 'rxjs';
import { Router } from '@angular/router';
import { InventoryFilterDrawerComponent } from '../inventory-filter-drawer/inventory-filter-drawer.component';
import { TablePaginationConfig } from '@app/utils/interfaces/table-pagination.interface';
import { TABLE_PAGINATION_DEFAULT_CONFIG } from '@app/utils/constants/table-pagination.constants';
import { TablePaginationComponent } from '@app/components/table-pagination/table-pagination.component';
import { IconComponent } from '@app/components/shared/icon/icon.component';
import { InventoryService } from '@app/services/inventory/inventory.service';
import { IconName } from '@app/utils/enums/icon-enum';
import {
  InventoryItem,
  InventoryQueryParams,
  InventoryPaginatedResponse,
  SortedInventoryOptions,
  SortedOptions,
} from '@app/utils/interfaces/inventory-item.interface';
import { AlternatingRowDirective } from '@app/components/shared/directives/alternating-row.directive';
import { StatusModalComponent } from '@app/components/shared/status-modal/status-modal.component';
import {
  TableColumn,
  INVENTORY_TABLE_COLUMNS,
} from '@app/utils/constants/inventory-table.constant';
import { AppRoutes } from '@app/utils/enums/routes.enum';
import { hasActiveFilters } from '@app/utils/helpers/inventory-filter.helpers';
import { HttpErrorResponse } from '@angular/common/http';
import { HttpStatusCode } from '@app/utils/enums/http-status-codes.enum';

@Component({
  selector: 'app-inventory-table',
  standalone: true,
  imports: [
    InventoryTableFunctionsComponent,
    TranslatePipe,
    IconComponent,
    MatProgressSpinnerModule,
    InventoryItemComponent,
    FormsModule,
    TablePaginationComponent,
    AlternatingRowDirective,
    InventoryFilterDrawerComponent,
  ],
  templateUrl: './inventory-table.component.html',
  styleUrl: './inventory-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InventoryTableComponent implements OnInit {
  private readonly inventoryService = inject(InventoryService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  public readonly iconName = IconName;
  public readonly columns: TableColumn[] = INVENTORY_TABLE_COLUMNS;

  public allItems = signal<InventoryItem[]>([]);
  public isLoading = signal<boolean>(false);
  public searchTerm = signal<string>('');
  public inventoriesPagination = signal<TablePaginationConfig>(TABLE_PAGINATION_DEFAULT_CONFIG);
  public activeFilters = signal<InventoryQueryParams>({});

  public readonly filterDrawer = viewChild(InventoryFilterDrawerComponent);

  public readonly inventoryItems = computed(() => this.allItems());
  public readonly showPagination = computed(() => Boolean(this.allItems().length));
  public readonly isSearchActive = computed(() => this.searchTerm().length > 0);
  public readonly hasActiveFilters = computed(() => hasActiveFilters(this.activeFilters()));

  private sortingColumn = '';
  private isDesc = false;
  private selectedSortings: SortedOptions[] = [];

  public readonly isSearchEmpty = computed(
    () =>
      !this.isLoading() &&
      this.allItems().length === 0 &&
      (this.isSearchActive() || this.hasActiveFilters()),
  );

  constructor() {
    effect(() => {
      const trigger = this.inventoryService.refreshTrigger();

      if (trigger > 0) {
        untracked(() => {
          this.fetchData();
        });
      }
    });

    effect(() => {
      const term = this.searchTerm();

      untracked(() => {
        if (term.trim().length >= 0) {
          this.inventoriesPagination.update((p) => ({ ...p, currentPage: 1 }));
          this.fetchData();
        }
      });
    });
  }

  public ngOnInit(): void {
    this.fetchData();
  }

  public onSearch(term: string): void {
    const trimmed = term.trim();

    if (trimmed.length >= 0) {
      this.searchTerm.set(trimmed);
    }
  }

  public handlePageChange(page: number): void {
    this.inventoriesPagination.update((p) => ({ ...p, currentPage: page }));
    this.fetchData();
  }

  protected handlePageSizeChange(pageSize: number): void {
    this.inventoriesPagination.update((p) => ({ ...p, pageSize, currentPage: 1 }));
    this.fetchData();
  }

  public openViewItem(item: InventoryItem): void {
    this.dialog.open(InventoryItemModalComponent, {
      data: { mode: 'view', id: item.id },
    });
  }

  public openEditItem(item: InventoryItem): void {
    this.router.navigate([AppRoutes.INVENTORY_EDIT, item.id]);
  }

  public openFilterDrawer(): void {
    this.filterDrawer()?.open();
  }

  public onFilterApplied(filterParams: InventoryQueryParams): void {
    this.activeFilters.set(filterParams);
    this.inventoriesPagination.update((p) => ({ ...p, currentPage: 1 }));
    this.fetchData();
  }

  public onFilterReset(): void {
    this.activeFilters.set({});
    this.inventoriesPagination.update((p) => ({ ...p, currentPage: 1 }));
    this.fetchData();
  }

  public removeFilter(key: string | number): void {
    this.activeFilters.update((filters) => ({
      ...filters,
      [key]: undefined,
    }));

    this.inventoriesPagination.update((p) => ({ ...p, currentPage: 1 }));
    this.fetchData();
  }

  private fetchData(): void {
    const { currentPage, pageSize } = this.inventoriesPagination();
    const filters = this.activeFilters();
    const search = this.searchTerm();

    const params: InventoryQueryParams = {
      pageNumber: currentPage,
      pageSize: pageSize,
      ...filters,
    };

    if (search) {
      params.upcOrName = search;
    }

    this.isLoading.set(true);

    this.inventoryService
      .getInventoryItems(params)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((res: InventoryPaginatedResponse) => {
          const items = res?.data?.items ?? [];
          const totalAmount = res?.data?.totalAmount ?? 0;

          this.allItems.set(items);
          this.inventoriesPagination.update((p) => ({
            ...p,
            totalItems: totalAmount,
          }));

          if (items.length === 0 && this.hasActiveFilters()) {
            this.openErrorModal(
              'inventory.apiErrors.noItemsFoundTitle',
              'inventory.apiErrors.noItemsFoundMessage',
            );
          }

          const isEmpty = items.length === 0 && !this.isSearchActive() && !this.hasActiveFilters();

          this.inventoryService.setIsEmpty(isEmpty);
        }),
        finalize(() => {
          this.isLoading.set(false);
        }),
      )
      .subscribe({
        error: (error: HttpErrorResponse) => {
          if (this.isSearchValidationError(error)) {
            this.handleInvalidSearch();
            return;
          }

          this.allItems.set([]);
          this.openErrorModal();
        },
      });
  }

  private openErrorModal(
    title = 'inventory.apiErrors.serverErrorTitle',
    message = 'inventory.apiErrors.serverErrorMessage',
  ): void {
    this.dialog.open(StatusModalComponent, {
      data: {
        isSuccess: false,
        title,
        description: message,
      },
    });
  }

  private isSearchValidationError(error: HttpErrorResponse): boolean {
    return error.status === HttpStatusCode.BAD_REQUEST && error.error?.errors?.upcorname;
  }

  private handleInvalidSearch(): void {
    this.allItems.set([]);
    this.inventoriesPagination.update((p) => ({
      ...p,
      totalItems: 0,
    }));
  }

  public sortByColumn(columnKey: string): void {
    const { currentPage, pageSize } = this.inventoriesPagination();
    const filters = this.activeFilters();
    this.sortStateManagement(columnKey);

    const params: SortedInventoryOptions = {
      pageNumber: currentPage,
      pageSize: pageSize,
      SortingOptions: [...this.selectedSortings],
      ...filters,
    };

    this.inventoryService
      .getSortedInventoryItems(params)
      .pipe(
        tap((res) => {
          const items = res?.data?.items ?? [];
          this.allItems.set(items);
        }),
        catchError(() => {
          this.openErrorModal();
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private sortStateManagement(columnKey: string): void {
    const existingSorting = this.selectedSortings.find((sort) => {
      return sort.field === columnKey;
    });

    if (this.sortingColumn === columnKey) {
      this.isDesc = !this.isDesc;
    } else {
      this.sortingColumn = columnKey;
      this.isDesc = true;
    }

    const newSort: SortedOptions = {
      field: columnKey,
      isDescending: this.isDesc,
    };

    if (existingSorting) {
      existingSorting.isDescending = this.isDesc;
    } else {
      this.selectedSortings = [...this.selectedSortings, newSort];
    }
  }
}
